import { useCallback, useEffect, useState } from "react";
import api, { apiErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { eur, pct } from "@/lib/format";
import { OPERATION_STATUS_CLS } from "@/lib/constants";
import { PageHeader } from "@/components/shared";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { toast } from "sonner";
import { Activity, CheckCircle2, TrendingUp, Wallet, Percent, Clock, Trophy, FileDown, Download } from "lucide-react";
import { ComposedChart, Bar, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from "recharts";

const Kpi = ({ icon: Icon, label, value, tone = "text-primary", testid }) => (
  <Card className="p-5" data-testid={testid}>
    <div className="flex items-center gap-3">
      <div className={`h-10 w-10 rounded-xl bg-primary/10 grid place-items-center ${tone}`}><Icon className="h-5 w-5" /></div>
      <div className="min-w-0">
        <div className="text-xs font-semibold text-slate-500 uppercase tracking-wide">{label}</div>
        <div className="text-lg font-bold text-slate-800 truncate">{value}</div>
      </div>
    </div>
  </Card>
);

export default function Performance() {
  const { isAdmin } = useAuth();
  const [year, setYear] = useState("all");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(() => {
    setLoading(true);
    api.get(`/performance?year=${year}`)
      .then((r) => setData(r.data))
      .catch((e) => toast.error(apiErr(e.response?.data?.detail) || "Errore di caricamento"))
      .finally(() => setLoading(false));
  }, [year]);
  useEffect(() => { load(); }, [load]);

  const exportPdf = async () => {
    try {
      const r = await api.get(`/performance/pdf?year=${year}`, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url; a.download = `performance_${year === "all" ? "tutti" : year}.pdf`;
      document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
    } catch { toast.error("Esportazione PDF non riuscita"); }
  };

  const exportCsv = () => {
    if (!data) return;
    const k = data.kpis;
    const rows = [];
    rows.push(["Report Performance", data.year_label || ""]);
    rows.push([]);
    rows.push(["KPI", "Valore"]);
    rows.push(["Operazioni totali", k.operations_total]);
    rows.push(["Operazioni attive", k.operations_active]);
    rows.push(["Operazioni concluse", k.operations_concluded]);
    rows.push(["Fatturato concluso", k.total_revenue]);
    rows.push(["Utile totale", k.total_profit]);
    rows.push(["Utile medio", Math.round(k.avg_profit)]);
    rows.push(["ROI medio %", k.avg_roi]);
    rows.push([]);
    rows.push(["Operazioni per stato", "Conteggio"]);
    data.by_status.forEach((s) => rows.push([s.status, s.count]));
    rows.push([]);
    rows.push(["Andamento mensile", "Concluse", "Utile"]);
    (data.monthly || []).forEach((m) => rows.push([m.month, m.concluded, m.profit]));
    rows.push([]);
    rows.push(["Classifica agenti", "Operazioni", "Concluse", "Fatturato", "Utile"]);
    data.leaderboard.forEach((a) => rows.push([a.agent, a.operations, a.concluded, a.revenue, a.profit]));
    const csv = rows.map((r) => r.map((c) => `"${String(c ?? "").replace(/"/g, '""')}"`).join(",")).join("\n");
    const blob = new Blob(["\uFEFF" + csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = `performance_${year === "all" ? "tutti" : year}.csv`;
    document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
  };

  const k = data?.kpis;
  const maxStatus = Math.max(1, ...(data?.by_status || []).map((s) => s.count));
  const hasMonthly = (data?.monthly || []).some((m) => m.concluded || m.profit);

  return (
    <div data-testid="performance-page">
      <PageHeader title="Performance" subtitle="Statistiche su operazioni, utili e tempi medi">
        <Select value={year} onValueChange={setYear}>
          <SelectTrigger className="w-40" data-testid="perf-year-select"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tutti gli anni</SelectItem>
            {(data?.years || []).map((y) => <SelectItem key={y} value={String(y)}>{y}</SelectItem>)}
          </SelectContent>
        </Select>
        <Button variant="outline" onClick={exportCsv} data-testid="perf-export-csv"><Download className="h-4 w-4 mr-2" />CSV</Button>
        <Button variant="outline" onClick={exportPdf} data-testid="perf-export-pdf"><FileDown className="h-4 w-4 mr-2" />PDF</Button>
      </PageHeader>

      {loading || !data ? (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">{Array.from({ length: 8 }).map((_, i) => <Skeleton key={i} className="h-20 rounded-xl" />)}</div>
      ) : (
        <div className="space-y-6">
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            <Kpi icon={Activity} label="Operazioni totali" value={k.operations_total} testid="kpi-total" />
            <Kpi icon={Clock} label="Operazioni attive" value={k.operations_active} testid="kpi-active" />
            <Kpi icon={CheckCircle2} label="Operazioni concluse" value={k.operations_concluded} tone="text-emerald-600" testid="kpi-concluded" />
            <Kpi icon={Wallet} label="Fatturato concluso" value={eur(k.total_revenue)} testid="kpi-revenue" />
            <Kpi icon={TrendingUp} label="Utile totale" value={eur(k.total_profit)} tone="text-emerald-600" testid="kpi-profit" />
            <Kpi icon={TrendingUp} label="Utile medio / operazione" value={eur(k.avg_profit)} testid="kpi-avg-profit" />
            <Kpi icon={Percent} label="ROI medio" value={pct(k.avg_roi)} testid="kpi-avg-roi" />
          </div>

          <Card className="p-6" data-testid="perf-monthly-chart">
            <h3 className="font-semibold text-slate-800 mb-4 flex items-center gap-2"><TrendingUp className="h-4 w-4 text-primary" />Andamento mensile ({data.year_label})</h3>
            {!hasMonthly ? (
              <div className="text-sm text-muted-foreground py-8 text-center">Nessuna operazione conclusa nel periodo selezionato.</div>
            ) : (
              <div className="h-72 w-full">
                <ResponsiveContainer width="100%" height="100%">
                  <ComposedChart data={data.monthly} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" vertical={false} />
                    <XAxis dataKey="month" tick={{ fontSize: 12, fill: "#64748B" }} />
                    <YAxis yAxisId="left" allowDecimals={false} tick={{ fontSize: 12, fill: "#64748B" }} />
                    <YAxis yAxisId="right" orientation="right" tick={{ fontSize: 11, fill: "#64748B" }} tickFormatter={(v) => `${Math.round(v / 1000)}k`} />
                    <Tooltip formatter={(value, name) => name === "Utile" ? eur(value) : value} contentStyle={{ borderRadius: 12, border: "1px solid #E2E8F0", fontSize: 12 }} />
                    <Legend wrapperStyle={{ fontSize: 12 }} />
                    <Bar yAxisId="left" dataKey="concluded" name="Operazioni concluse" fill="#FC82ED" radius={[4, 4, 0, 0]} barSize={22} />
                    <Line yAxisId="right" type="monotone" dataKey="profit" name="Utile" stroke="#0F766E" strokeWidth={2.5} dot={{ r: 3 }} />
                  </ComposedChart>
                </ResponsiveContainer>
              </div>
            )}
          </Card>

          <div className="grid gap-6 lg:grid-cols-2">
            <Card className="p-6" data-testid="perf-by-status">
              <h3 className="font-semibold text-slate-800 mb-4 flex items-center gap-2"><Activity className="h-4 w-4 text-primary" />Operazioni per stato</h3>
              <div className="space-y-2.5">
                {data.by_status.map((s) => (
                  <div key={s.status} className="flex items-center gap-3" data-testid={`status-row-${s.status}`}>
                    <span className={`inline-flex shrink-0 w-52 rounded-full border px-2.5 py-0.5 text-xs font-semibold ${OPERATION_STATUS_CLS[s.status] || "bg-slate-100 text-slate-700 border-slate-200"}`}>{s.status}</span>
                    <div className="h-2 flex-1 rounded-full bg-muted overflow-hidden">
                      <div className="h-full rounded-full bg-primary/70" style={{ width: `${(s.count / maxStatus) * 100}%` }} />
                    </div>
                    <span className="w-6 text-right text-sm font-semibold text-slate-700 tabular-nums">{s.count}</span>
                  </div>
                ))}
              </div>
            </Card>

            <Card className="p-6" data-testid="perf-phases">
              <h3 className="font-semibold text-slate-800 mb-4 flex items-center gap-2"><Clock className="h-4 w-4 text-primary" />Tempo medio per fase</h3>
              <div className="grid grid-cols-2 gap-3">
                {data.phases.map((ph) => (
                  <div key={ph.label} className="rounded-lg border border-border p-3" data-testid={`phase-${ph.label}`}>
                    <div className="text-xs font-semibold text-slate-500">{ph.label}</div>
                    <div className="mt-1 text-2xl font-bold text-slate-800">{ph.avg_days == null ? "—" : ph.avg_days}<span className="text-sm font-medium text-muted-foreground"> gg</span></div>
                    <div className="text-xs text-muted-foreground">{ph.count} operazioni</div>
                  </div>
                ))}
              </div>
            </Card>
          </div>

          <Card className="p-6" data-testid="perf-leaderboard">
            <h3 className="font-semibold text-slate-800 mb-4 flex items-center gap-2"><Trophy className="h-4 w-4 text-primary" />Classifica {isAdmin ? "agenti" : "personale"}</h3>
            {data.leaderboard.length === 0 ? (
              <div className="text-sm text-muted-foreground">Nessuna operazione nel periodo selezionato.</div>
            ) : (
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader><TableRow>
                    <TableHead>Agente</TableHead>
                    <TableHead className="text-right">Operazioni</TableHead>
                    <TableHead className="text-right">Concluse</TableHead>
                    <TableHead className="text-right">Fatturato</TableHead>
                    <TableHead className="text-right">Utile</TableHead>
                  </TableRow></TableHeader>
                  <TableBody>
                    {data.leaderboard.map((a, i) => (
                      <TableRow key={a.agent} data-testid={`leader-row-${i}`}>
                        <TableCell className="font-medium text-slate-800">{i === 0 && a.profit > 0 && <Trophy className="inline h-3.5 w-3.5 text-amber-500 mr-1.5" />}{a.agent}</TableCell>
                        <TableCell className="text-right tabular-nums">{a.operations}</TableCell>
                        <TableCell className="text-right tabular-nums">{a.concluded}</TableCell>
                        <TableCell className="text-right font-mono text-sm">{eur(a.revenue)}</TableCell>
                        <TableCell className="text-right font-mono text-sm font-semibold text-emerald-700">{eur(a.profit)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </Card>
        </div>
      )}
    </div>
  );
}
